import { useCallback, useState } from 'react';

import { useRenameAnalysisMutation } from '../lib/api/useRenameAnalysisMutation';

interface UseRenameAnalysisDialogArgs {
  projectId: string;
  analysisProfileId: string;
  currentName: string;
  onClose: () => void;
}

export interface UseRenameAnalysisDialogResult {
  name: string;
  onChangeName: (id: string, value: string) => void;
  onSubmit: () => Promise<void>;
  closeDialog: () => void;
  isPending: boolean;
  submittingError: { message: string } | undefined;
  isValidToSubmit: boolean;
}

/**
 * Behaviour for the rename-analysis dialog: name entry (pre-filled with the
 * current name), client-side validation (non-empty, changed) and the rename
 * mutation. Intl-free so the view owns copy. No duplicate-name check — analyses
 * in a project may share a name.
 */
export const useRenameAnalysisDialog = ({
  projectId,
  analysisProfileId,
  currentName,
  onClose,
}: UseRenameAnalysisDialogArgs): UseRenameAnalysisDialogResult => {
  const [name, setName] = useState(currentName);
  const { mutateAsync, isPending, error, reset } = useRenameAnalysisMutation();

  const onChangeName = useCallback((_id: string, value: string) => {
    setName(value);
  }, []);

  const trimmed = name.trim();

  const onSubmit = useCallback(async () => {
    if (!trimmed || trimmed === currentName) return;
    // Let a rejection propagate so the dialog shows the error instead of closing.
    await mutateAsync({ projectId, analysisProfileId, name: trimmed });
  }, [projectId, analysisProfileId, trimmed, currentName, mutateAsync]);

  const closeDialog = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return {
    name,
    onChangeName,
    onSubmit,
    closeDialog,
    isPending,
    submittingError: error ? { message: error.message } : undefined,
    isValidToSubmit: Boolean(trimmed) && trimmed !== currentName,
  };
};
